import React from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useRoles } from './useRoles';
import useSessionStore from '../state/session';
import { getSupabaseClient } from '../data/supabase';
import { fetchAiRuntimeSettings, type AIRuntimeSettings } from '../data/aiSettings';

export function useAISettings(supabase?: SupabaseClient) {
  const [record, setRecord] = React.useState<AIRuntimeSettings | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const session = useSessionStore((s) => s.session);
  const canQuery = !!session?.user?.id;
  const client = supabase ?? getSupabaseClient();

  const refresh = React.useCallback(async () => {
    if (!canQuery) {
      setRecord(null);
      setError(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const { data, error } = await fetchAiRuntimeSettings({ supabase: client });
      if (error) throw error;
      const rec = data || null;
      setRecord(rec);
      setError(null);
      return rec;
    } catch (err: any) {
      setError(err?.message || 'Unable to load AI settings');
      setRecord(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [canQuery, client]);

  React.useEffect(() => {
    if (!canQuery) {
      setRecord(null);
      setError(null);
      setLoading(false);
      return;
    }
    refresh();
  }, [canQuery, refresh]);

  return { record, loading, error, refresh };
}

export function useAIEnabled(supabase?: SupabaseClient) {
  const { record, loading, error, refresh } = useAISettings(supabase);
  const enabled = !!record?.ai_enabled;
  return { enabled, loading, error, refresh };
}

// Feature-level enablement with admin override.
// Flags (defaults true if missing):
//  - feature_venue_add_enabled
//  - feature_bizcard_enabled
//  - feature_item_research_enabled
export function useAIFeatures(supabase?: SupabaseClient) {
  const { isAdmin, isUser } = useRoles();
  const { record, loading, error, refresh } = useAISettings(supabase);
  const flags = record?.flags ?? {};
  const globalOn = !!record?.ai_enabled;
  const venueAddFlag = flags.feature_venue_add_enabled !== false; // default true
  const bizCardFlag = flags.feature_bizcard_enabled !== false; // default true
  const venueSearchFlag = flags.feature_venue_search_enabled !== false; // default true
  const guidelinesFlag = flags.feature_guidelines_enabled !== false; // default true
  const avMenuUploadsFlag = flags.feature_av_menu_uploads_enabled !== false; // default true
  const formImportFlag = flags.feature_form_import_enabled !== false; // default true
  const signatureFieldPlacementFlag =
    flags.feature_signature_field_placement_enabled !== false; // default true
  const itemResearchFlag = flags.feature_item_research_enabled !== false; // default true
  const inventoryQueryFlag = flags.feature_inventory_query_enabled !== false; // default true
  const beoImportFlag = flags.feature_beo_import_enabled !== false; // default true
  // AI Add Venue: available to Users and up when globally enabled; Admins/Super Admins always on
  const venueAddEnabled = isAdmin ? true : (isUser && globalOn && venueAddFlag);
  const bizCardEnabled = isAdmin ? true : (globalOn && bizCardFlag);
  const venueSearchEnabled = isAdmin ? true : (globalOn && venueSearchFlag);
  const guidelinesEnabled = isAdmin ? true : (globalOn && guidelinesFlag);
  const avMenuUploadsEnabled = isAdmin ? true : (globalOn && avMenuUploadsFlag);
  const formImportEnabled = isAdmin ? true : (globalOn && formImportFlag);
  // This workflow has an explicit operational kill switch. Unlike the other
  // support tools, admins should see the same disabled state the server enforces.
  const signatureFieldPlacementEnabled = globalOn && signatureFieldPlacementFlag;
  const itemResearchEnabled = isAdmin ? true : (globalOn && itemResearchFlag);
  const inventoryQueryEnabled = isAdmin ? true : (globalOn && inventoryQueryFlag);
  const beoImportEnabled = isAdmin ? true : (globalOn && beoImportFlag);
  return {
    venueAddEnabled,
    bizCardEnabled,
    venueSearchEnabled,
    guidelinesEnabled,
    avMenuUploadsEnabled,
    formImportEnabled,
    signatureFieldPlacementEnabled,
    itemResearchEnabled,
    inventoryQueryEnabled,
    beoImportEnabled,
    loading,
    error,
    refresh,
  };
}
